import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import SectionTitle from "../components/SectionTitle";
import mockStats from "../data/mockStats";

function useCountUp(target, duration = 1400, start) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (!start) return;

        const reduceMotion =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (reduceMotion) {
            setValue(target);
            return;
        }

        let startTs = null;
        let frameId = null;

        function step(ts) {
            if (!startTs) startTs = ts;
            const progress = Math.min((ts - startTs) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            setValue(Math.floor(eased * target));

            if (progress < 1) {
                frameId = requestAnimationFrame(step);
            }
        }

        frameId = requestAnimationFrame(step);

        return () => {
            if (frameId) cancelAnimationFrame(frameId);
        };
    }, [target, duration, start]);

    return value;
}

function StatCard({ stat, start }) {
    const number = useCountUp(stat.rawValue ?? 0, 1400, start);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2.25, sm: 2.5, md: 2.75, lg: 3 },
                borderRadius: { xs: 1, md: 1 },
                border: "1px solid rgba(255,255,255,0.10)",
                bgcolor: "rgba(255,255,255,0.02)",
                height: "100%",
                width: "100%",
                minHeight: { xs: 185, sm: 195, md: 210 },
                transition:
                    "transform .25s ease, border-color .25s ease, box-shadow .25s ease",
                backdropFilter: "blur(8px)",
                display: "flex",
                "&:hover": {
                    borderColor: "rgba(172,201,14,0.35)",
                    boxShadow:
                        "0 0 0 1px rgba(172,201,14,0.16) inset, 0 18px 50px rgba(0,0,0,0.22)",
                    transform: "translateY(-4px)",
                },
            }}
        >
            <Stack
                spacing={{ xs: 1, sm: 1.2, md: 1.4 }}
                sx={{
                    justifyContent: "space-between",
                    width: "100%",
                }}
            >
                <Typography
                    sx={{
                        color: "rgba(255,255,255,0.62)",
                        fontSize: { xs: "0.72rem", sm: "0.76rem", md: "0.82rem" },
                        textTransform: "uppercase",
                        letterSpacing: { xs: "0.14em", md: "0.16em" },
                        fontWeight: 900,
                        lineHeight: 1.35,
                    }}
                >
                    {stat.label}
                </Typography>

                <Typography
                    sx={{
                        color: "var(--verdeClaro)",
                        fontWeight: 1000,
                        fontSize: {
                            xs: "clamp(1.9rem, 7vw, 2.35rem)",
                            sm: "clamp(2.1rem, 5vw, 2.6rem)",
                            md: "clamp(2.2rem, 3vw, 2.8rem)",
                        },
                        lineHeight: 1.05,
                        letterSpacing: "-0.03em",
                        wordBreak: "break-word",
                    }}
                >
                    {stat.prefix}
                    {number.toLocaleString("pt-BR")}
                    {stat.suffix}
                </Typography>

                {!!stat.caption && (
                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.76)",
                            fontSize: { xs: "0.92rem", sm: "0.95rem", md: "0.98rem" },
                            lineHeight: { xs: 1.5, md: 1.6 },
                            maxWidth: "30ch",
                        }}
                    >
                        {stat.caption}
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
}

export default function Numeros() {
    const stats = useMemo(() => mockStats, []);
    const sectionRef = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.22,
                rootMargin: "0px 0px -40px 0px",
            }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            id="numeros"
            ref={sectionRef}
            sx={{
                position: "relative",
                width: "100%",
                overflow: "hidden",
                py: {
                    xs: 8,
                    sm: 9,
                    md: 10,
                    lg: 11,
                },
                bgcolor: "var(--preto)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: "-10px auto 0 1400px",
                    width: { xs: 240, sm: 320, md: 420, lg: 520 },
                    height: { xs: 240, sm: 320, md: 420, lg: 520 },
                    borderRadius: "50%",
                    pointerEvents: "none",
                    background:
                        "radial-gradient(circle, rgba(172,201,14,0.04) 0%, transparent 70%)",
                    filter: "blur(8px)",
                }}
            />

            <Container
                maxWidth={false}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    px: { xs: 2, md: 3 },
                    maxWidth: 1600,
                    mx: "auto",
                }}
            >
                <Box
                    sx={{
                        mb: { xs: 3, sm: 4, md: 5 },
                        maxWidth: { xs: "100%", md: 980 },
                    }}
                >
                    <SectionTitle
                        eyebrow="autoridade"
                        title="Resultados que sustentam a percepção"
                        subtitle="Mais do que volume, números que comprovam consistência, impacto e presença."
                    />
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        width: "100%",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: {
                            xs: 2,
                            sm: 2,
                            md: 2.5,
                            lg: 3,
                        },
                        alignItems: "stretch",
                    }}
                >
                    {stats.map((s) => (
                        <Box key={s.id} sx={{ minWidth: 0 }}>
                            <StatCard stat={s} start={visible} />
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
}